/**
 * Advisory copy of the recurrence rules, so the form can show a live count and
 * refuse to submit before the club has typed a date the API will reject.
 *
 * THE API IS THE AUTHORITY (lambda/lib/recurrence.ts). This exists only so the
 * count under the date field updates as they pick, rather than making them
 * submit to find out. If the two ever disagree, the API wins and the form is
 * the thing that's wrong.
 *
 * Dates stay Y/M/D throughout: `new Date('2026-09-07')` is midnight UTC, and
 * formatting that locally east of Greenwich shows the previous day.
 */

export const MAX_OCCURRENCES = 60;
export const MAX_MONTHS_AHEAD = 12;

export type Freq = 'daily' | 'weekly' | 'monthly';

export const WEEKDAYS = [
  { value: 1, short: 'Mon' }, { value: 2, short: 'Tue' }, { value: 3, short: 'Wed' },
  { value: 4, short: 'Thu' }, { value: 5, short: 'Fri' }, { value: 6, short: 'Sat' },
  { value: 0, short: 'Sun' },
];

function isLeap(y: number) { return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0; }

function daysInMonth(y: number, m: number): number {
  const table = [31, isLeap(y) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return table[m - 1] ?? 30;
}

// Indexed access is checked in this project's tsconfig, so the parts are read
// explicitly rather than destructured out of a mapped array.
function parse(d: string): [number, number, number] | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return null;
  const parts = d.split('-');
  const y = Number(parts[0]); const m = Number(parts[1]); const day = Number(parts[2]);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(day)) return null;
  if (m < 1 || m > 12 || day < 1 || day > daysInMonth(y, m)) return null;
  return [y, m, day];
}

function ord(ymd: [number, number, number]): number {
  return Math.floor(Date.UTC(ymd[0], ymd[1] - 1, ymd[2]) / 86400000);
}

function weekday(n: number) { return new Date(n * 86400000).getUTCDay(); }

function addMonths(ymd: [number, number, number], n: number): [number, number, number] {
  const t = ymd[0] * 12 + (ymd[1] - 1) + n;
  const ny = Math.floor(t / 12); const nm = (t % 12) + 1;
  return [ny, nm, Math.min(ymd[2], daysInMonth(ny, nm))];
}

export interface Preview {
  count: number;
  /** null when the selection is fine; a sentence to show when it is not. */
  problem: string | null;
  /** The latest end date this start allows, for the input's `max`. */
  maxUntil: string;
}

export function previewRecurrence(
  startDate: string, freq: Freq, until: string, byWeekday: number[],
): Preview {
  const start = parse(startDate);
  const empty = { count: 0, problem: null as string | null, maxUntil: '' };
  if (!start) return empty;

  const horizon = addMonths(start, MAX_MONTHS_AHEAD);
  const maxUntil = `${horizon[0]}-${String(horizon[1]).padStart(2, '0')}-${String(horizon[2]).padStart(2, '0')}`;

  const end = parse(until);
  if (!end) return { ...empty, maxUntil };

  const s = ord(start); const u = ord(end);
  if (u < s) return { count: 0, problem: 'The end date is before the start date.', maxUntil };
  if (u > ord(addMonths(start, MAX_MONTHS_AHEAD))) {
    return { count: 0, problem: `Repeats can run for at most ${MAX_MONTHS_AHEAD} months.`, maxUntil };
  }

  let count = 0;
  if (freq === 'monthly') {
    while (ord(addMonths(start, count)) <= u) count++;
  } else {
    const wanted = freq === 'weekly'
      ? new Set(byWeekday.length ? byWeekday : [weekday(s)])
      : null;
    if (wanted && wanted.size === 0) {
      return { count: 0, problem: 'Pick at least one day of the week.', maxUntil };
    }
    for (let i = s; i <= u; i++) {
      if (wanted && i !== s && !wanted.has(weekday(i))) continue;
      count++;
    }
  }

  if (count > MAX_OCCURRENCES) {
    return {
      count,
      problem: `That would create ${count} events. The maximum is ${MAX_OCCURRENCES} — choose an earlier end date.`,
      maxUntil,
    };
  }
  return { count, problem: null, maxUntil };
}
